import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  MultiFactorResolver,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator,
  updatePassword
} from 'firebase/auth';
import { Field, FieldProps, Form, Formik } from 'formik';

import { Input, OtpInput } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { showToast, ToastType } from '@/helpers/toast';
import { updatePasswordSchema } from '@/pages/ProfilePage/UpdatePasswordValidation';
import {
  ShowPasswordIcon as HidePasswordIcon,
  PasswordShownIcon as ShowPasswordIcon
} from '@/resources';
import { TestIds } from '@/shared/test-ids';
import { UpdatePassword } from '@/types';

export const NewPassword = () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [t] = useTranslation('common');

  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false
  });

  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);

  const pendingNewPasswordRef = useRef('');
  const resetFormRef = useRef<(() => void) | null>(null);

  const handleShowPassword = (field: 'currentPassword' | 'newPassword' | 'confirmNewPassword') => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field]
    }));
  };

  const getInitialValues = () => ({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const finishPasswordUpdate = async () => {
    if (!currentUser) {
      return;
    }
    await updatePassword(currentUser, pendingNewPasswordRef.current);
    showToast(ToastType.SUCCESS, t('passwordUpdatedSuccessfully'));
    resetFormRef.current?.();
  };

  const handleSubmit = async (values: UpdatePassword, { resetForm }: { resetForm: () => void }) => {
    if (!currentUser) {
      showToast(ToastType.ERROR, t('userNotAuthenticated'));
      return;
    }

    const currentEmail = currentUser.email || '';
    const credential = EmailAuthProvider.credential(currentEmail, values.currentPassword);

    pendingNewPasswordRef.current = values.newPassword;
    resetFormRef.current = resetForm;

    try {
      await reauthenticateWithCredential(currentUser, credential);
      await finishPasswordUpdate();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/multi-factor-auth-required') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resolver = getMultiFactorResolver(auth, err as any);
        setMfaResolver(resolver);
      } else {
        showToast(ToastType.ERROR, t('incorrectCurrentPassword'));
      }
    }
  };

  const handleVerifyTotp = async () => {
    if (!mfaResolver) {
      return;
    }
    setIsVerifyingTotp(true);
    try {
      const totpFactor = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (!totpFactor) {
        throw new Error('No TOTP factor');
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpFactor.uid, totpCode);
      await mfaResolver.resolveSignIn(assertion);
      await finishPasswordUpdate();

      setMfaResolver(null);
      setTotpCode('');
    } catch {
      showToast(ToastType.ERROR, t('mfaInvalidCode'));
      setTotpCode('');
    } finally {
      setIsVerifyingTotp(false);
    }
  };

  const handleCancelMfa = () => {
    setMfaResolver(null);
    setTotpCode('');
  };

  const getShowPasswordIcon = (
    fieldValue: string,
    showPassword: boolean,
    handleShowPassword: () => void
  ) => {
    return (
      <div
        className='absolute right-0 flex cursor-pointer items-center'
        onClick={handleShowPassword}>
        {showPassword ? (
          <ShowPasswordIcon
            className={`mr-2 text-xl ${fieldValue.length > 0 ? 'fill-primary-black' : 'fill-secondary-gray'}`}
          />
        ) : (
          <HidePasswordIcon
            className={`mr-2 text-xl ${fieldValue.length > 0 ? 'fill-primary-black' : 'fill-secondary-gray'}`}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <Label text={t('password')} type={LABEL_TYPE.H3} className='mb-3 text-primary-blue' />
      {mfaResolver ? (
        <div className='flex flex-col gap-3'>
          <Label
            text={t('mfaEnterCode')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='!text-secondary-gray'
          />
          <OtpInput
            value={totpCode}
            onChange={setTotpCode}
            disabled={isVerifyingTotp}
            testIdPrefix={TestIds.mfa.changePasswordOtpInput}
            onComplete={handleVerifyTotp}
          />
          <div className='flex gap-3'>
            <Button
              variant={ButtonVariant.SECONDARY}
              disabled={isVerifyingTotp}
              handleOnClick={handleCancelMfa}
              className='w-fit'>
              {t('cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          onSubmit={handleSubmit}
          validationSchema={updatePasswordSchema}>
          {({ errors, touched, dirty, isValid }) => (
            <Form id='confirmPassword'>
              <div className='space-y-5'>
                <div className='relative'>
                  <Field name='currentPassword'>
                    {({ field }: FieldProps) => (
                      <>
                        <Input
                          id='currentPassword'
                          label={t('currentPassword')}
                          type={showPassword.currentPassword ? 'text' : 'password'}
                          autoComplete='current-password'
                          placeholder={t('typeHere')}
                          errorMessage={errors.currentPassword}
                          isInvalid={!!errors.currentPassword && touched.currentPassword}
                          rightSideIcon={getShowPasswordIcon(
                            field.value,
                            showPassword.currentPassword,
                            () => handleShowPassword('currentPassword')
                          )}
                        />
                      </>
                    )}
                  </Field>
                </div>
                <div className='relative'>
                  <Field name='newPassword'>
                    {({ field }: FieldProps) => (
                      <>
                        <Input
                          id='newPassword'
                          label={t('newPassword')}
                          type={showPassword.newPassword ? 'text' : 'password'}
                          autoComplete='new-password'
                          placeholder={t('typeHere')}
                          errorMessage={errors.newPassword}
                          isInvalid={!!errors.newPassword && touched.newPassword}
                          rightSideIcon={getShowPasswordIcon(
                            field.value,
                            showPassword.newPassword,
                            () => handleShowPassword('newPassword')
                          )}
                        />
                      </>
                    )}
                  </Field>
                </div>
                <div className='relative'>
                  <Field name='confirmNewPassword'>
                    {({ field }: FieldProps) => (
                      <>
                        <Input
                          id='confirmNewPassword'
                          label={t('confirmNewPassword')}
                          type={showPassword.confirmNewPassword ? 'text' : 'password'}
                          autoComplete='new-password'
                          placeholder={t('typeHere')}
                          errorMessage={errors.confirmNewPassword}
                          isInvalid={!!errors.confirmNewPassword && touched.confirmNewPassword}
                          rightSideIcon={getShowPasswordIcon(
                            field.value,
                            showPassword.confirmNewPassword,
                            () => handleShowPassword('confirmNewPassword')
                          )}
                        />
                      </>
                    )}
                  </Field>
                </div>
              </div>
              <div className={'mt-5 flex justify-end pb-5 md:p-0'}>
                <Button
                  type='submit'
                  variant={ButtonVariant.PRIMARY}
                  disabled={!(dirty && isValid)}>
                  {t('resetPassword')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};
